import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Users, Star, CheckCircle, Clock, Award, MessageSquare } from "lucide-react";

const CommunityMentoring = () => {
  const { token, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [mentors, setMentors] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBecomeMentorModal, setShowBecomeMentorModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [selectedMentor, setSelectedMentor] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [mentorForm, setMentorForm] = useState({
    expertise: "",
    bio: "",
    experience_years: 1,
    availability: "Weekends"
  });

  const [requestMessage, setRequestMessage] = useState("");

  useEffect(() => {
    fetchMentors();
    fetchRequests();
  }, []);

  const fetchMentors = async () => {
    try {
      const response = await axios.get(`${API}/community/mentors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMentors(response.data);
    } catch (error) {
      toast.error("Failed to load mentors");
    } finally {
      setLoading(false);
    }
  };

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/community/mentorship/requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(response.data);
    } catch (error) {
      console.error("Failed to load requests");
    }
  };

  const handleBecomeMentor = async () => {
    try {
      const form = new FormData();
      form.append("expertise", mentorForm.expertise);
      form.append("bio", mentorForm.bio);
      form.append("experience_years", mentorForm.experience_years);
      form.append("availability", mentorForm.availability);

      await axios.post(`${API}/community/mentors`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("You're now a mentor!");
      setShowBecomeMentorModal(false);
      setMentorForm({ expertise: "", bio: "", experience_years: 1, availability: "Weekends" });
      fetchMentors();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to register as mentor");
    }
  };

  const handleRequestMentorship = async () => {
    try {
      const form = new FormData();
      form.append("mentor_id", selectedMentor.user_id);
      form.append("message", requestMessage);

      await axios.post(`${API}/community/mentorship/request`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Mentorship request sent!");
      setShowRequestModal(false);
      setRequestMessage("");
      fetchRequests();
    } catch (error) {
      toast.error("Failed to send request");
    }
  };

  const handleUpdateRequest = async (requestId, status) => {
    try {
      const form = new FormData();
      form.append("status", status);

      await axios.put(`${API}/community/mentorship/requests/${requestId}`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Request ${status}!`);
      fetchRequests();
    } catch (error) {
      toast.error("Failed to update request");
    }
  };

  const filteredMentors = mentors.filter(mentor =>
    (mentor.username && mentor.username.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (mentor.expertise && Array.isArray(mentor.expertise) && mentor.expertise.some(e => e && e.toLowerCase().includes(searchTerm.toLowerCase())))
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <Navbar />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading mentors...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-6 md:py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-md">
                <Users className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900">Peer Mentoring</h1>
                <p className="text-gray-600 text-sm md:text-base">Connect with experienced mentors</p>
              </div>
            </div>
            <Button onClick={() => setShowBecomeMentorModal(true)} className="shadow-md">
              <Award className="w-4 h-4 mr-2" />
              Become a Mentor
            </Button>
          </div>
        </div>

        <Tabs defaultValue="browse" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-8">
            <TabsTrigger value="browse">Browse Mentors</TabsTrigger>
            <TabsTrigger value="requests">My Requests ({requests.length})</TabsTrigger>
          </TabsList>

          {/* Browse Mentors Tab */}
          <TabsContent value="browse">
            {/* Search */}
            <Card className="mb-6 border-none shadow-md">
              <CardContent className="pt-6">
                <Input
                  placeholder="Search mentors by name or expertise..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </CardContent>
            </Card>

            {/* Mentors Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredMentors.length > 0 ? (
                filteredMentors.map(mentor => (
                  <Card key={mentor.id} className="border-none shadow-md hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-lg mb-1">{mentor.username}</CardTitle>
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                            <span>{mentor.rating ? mentor.rating.toFixed(1) : '0.0'}</span>
                            {mentor.is_verified && (
                              <CheckCircle className="w-4 h-4 text-primary ml-2" />
                            )}
                          </div>
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {mentor.experience_years}y exp
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground mb-4 line-clamp-3">{mentor.bio}</p>
                      
                      <div className="mb-4">
                        <p className="text-xs text-muted-foreground mb-2">Expertise:</p>
                        <div className="flex flex-wrap gap-2">
                          {mentor.expertise.slice(0, 3).map((exp, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs">{exp}</Badge>
                          ))}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-4">
                        <Clock className="w-3 h-3" />
                        <span>{mentor.availability}</span>
                      </div>

                      <Button
                        onClick={() => {
                          setSelectedMentor(mentor);
                          setShowRequestModal(true);
                        }}
                        className="w-full"
                        size="sm"
                      >
                        Request Mentorship
                      </Button>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <div className="col-span-full">
                  <Card className="border-none shadow-md">
                    <CardContent className="p-12 text-center">
                      <Users className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-semibold mb-2">No mentors found</h3>
                      <p className="text-muted-foreground">Try adjusting your search or check back later</p>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          </TabsContent>

          {/* My Requests Tab */}
          <TabsContent value="requests">
            <div className="space-y-4">
              {requests.length > 0 ? (
                requests.map(request => (
                  <Card key={request.id} className="border-none shadow-md">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h4 className="font-semibold">
                              {request.mentor_id === user.id ? `From: ${request.mentee_name}` : `To: ${request.mentor_name}`}
                            </h4>
                            <Badge
                              variant="outline"
                              className={
                                request.status === "accepted"
                                  ? "bg-green-100 text-green-700 border-green-200"
                                  : request.status === "rejected"
                                  ? "bg-red-100 text-red-700 border-red-200"
                                  : "bg-yellow-100 text-yellow-700 border-yellow-200"
                              }
                            >
                              {request.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mb-3">{request.message}</p>
                          
                          {request.mentor_id === user.id && request.status === "pending" && (
                            <div className="flex gap-2">
                              <Button
                                onClick={() => handleUpdateRequest(request.id, "accepted")}
                                size="sm"
                                variant="default"
                              >
                                Accept
                              </Button>
                              <Button
                                onClick={() => handleUpdateRequest(request.id, "rejected")}
                                size="sm"
                                variant="outline"
                              >
                                Reject
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <Card className="border-none shadow-md">
                  <CardContent className="p-12 text-center">
                    <MessageSquare className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No requests yet</h3>
                    <p className="text-muted-foreground">Start by requesting mentorship from experienced mentors</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Become Mentor Modal */}
        <Dialog open={showBecomeMentorModal} onOpenChange={setShowBecomeMentorModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Become a Mentor</DialogTitle>
              <DialogDescription>Share your knowledge and help others grow</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Expertise Areas (comma-separated)</Label>
                <Input
                  value={mentorForm.expertise}
                  onChange={(e) => setMentorForm({ ...mentorForm, expertise: e.target.value })}
                  placeholder="e.g., Programming, Career Planning, Study Skills"
                />
              </div>
              <div>
                <Label>Bio</Label>
                <Textarea
                  value={mentorForm.bio}
                  onChange={(e) => setMentorForm({ ...mentorForm, bio: e.target.value })}
                  placeholder="Tell us about yourself and why you want to be a mentor..."
                  rows={4}
                />
              </div>
              <div>
                <Label>Years of Experience</Label>
                <Input
                  type="number"
                  min="1"
                  value={mentorForm.experience_years}
                  onChange={(e) => setMentorForm({ ...mentorForm, experience_years: parseInt(e.target.value) })}
                />
              </div>
              <div>
                <Label>Availability</Label>
                <Input
                  value={mentorForm.availability}
                  onChange={(e) => setMentorForm({ ...mentorForm, availability: e.target.value })}
                  placeholder="e.g., Weekends, Evenings"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowBecomeMentorModal(false)}>Cancel</Button>
              <Button onClick={handleBecomeMentor}>Submit Application</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Request Mentorship Modal */}
        <Dialog open={showRequestModal} onOpenChange={setShowRequestModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Request Mentorship</DialogTitle>
              <DialogDescription>
                Send a mentorship request to {selectedMentor?.username}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Label>Your Message</Label>
              <Textarea
                value={requestMessage}
                onChange={(e) => setRequestMessage(e.target.value)}
                placeholder="Introduce yourself and explain why you'd like this person as your mentor..."
                rows={6}
                className="mt-2"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRequestModal(false)}>Cancel</Button>
              <Button onClick={handleRequestMentorship}>Send Request</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <Footer />
    </div>
  );
};

export default CommunityMentoring;